import React, { useEffect, useState } from "react";
import useAxios from "../../hooks/useAxios";
import { useSelector } from "react-redux";
import {
  formatIsoDate,
  renderStatus,
  formatName,
} from "../../utils/generalUtils";
import GeneratePdf from "../../generatePdf/GeneratePdf";
import { Select, Space } from "antd";
import SpectatorTable from "../../components/Table/SpectatorTable.js";

const SpectateCos = () => {
  const { response, loading, fetchData } = useAxios();
  const { spectatorCosPopup } = useSelector((state) => state.generalSlice);
  const { spectator_accesses } = useSelector(
    (state) => state.accountDetailSlice.details
  );

  const handleRefresh = (positionId) => {
    fetchData({ url: `/approver/cos/${positionId}`, method: "GET" });
  };

  // A Dean grant scoped "facsec" (Faculty Secretary View) is excluded —
  // that scope means FSR only, not TRL/COS/CWA.
  const mappedEmpPos = (spectator_accesses || [])
    .filter((item) => item?.scope !== "facsec")
    .map((item) => ({
      value: item?.approver_position_id,
      label: item?.approver_position
        ? `${item.approver_position.position_name} - ${item.approver_position.unit}`
        : `Position ${item?.approver_position_id}`,
    }));
  const hasActiveSpectatorPosition = mappedEmpPos.length > 0;

  const [selectedPos, setSelectedPos] = useState(
    mappedEmpPos.length > 0 ? mappedEmpPos[0].value : undefined
  );

  useEffect(() => {
    if (
      (selectedPos === undefined || selectedPos === null) &&
      mappedEmpPos.length > 0
    ) {
      setSelectedPos(mappedEmpPos[0].value);
    }
  }, [mappedEmpPos, selectedPos]);

  const handlePosSelect = (posId) => {
    setSelectedPos(posId);
  };

  useEffect(() => {
    if (selectedPos === undefined || selectedPos === null) {
      return;
    }

    handleRefresh(selectedPos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPos]);

  const mappedData =
    response?.data?.map((item, index) => ({
      key: index + 1,
      name: `${item?.submission?.submitter?.last_name || ""}, ${
        item?.submission?.submitter?.first_name || ""
      }`.trim(),
      formattedName: formatName(
        item?.submission?.submitter?.first_name,
        item?.submission?.submitter?.middle_name,
        item?.submission?.submitter?.last_name,
        "lfmcut"
      ),
      approver_name: `${item?.approver?.last_name || ""}, ${
        item?.approver?.first_name || ""
      }`.trim(),
      formattedApproverName: formatName(
        item?.approver?.first_name,
        item?.approver?.middle_name,
        item?.approver?.last_name,
        "lfmcut"
      ),
      ...item?.submission?.certificate_of_service,
      submitter_position: item?.submission?.position?.position_name,
      submitter_position_unit: item?.submission?.position?.unit,
      date_submitted: item?.submission?.created_at
        ? formatIsoDate(item?.submission?.created_at)
        : null,
      status: item?.status || "no data",
      submission_id: item?.submission?.id,
      approval_id: item?.id,
      approval_date: item?.approval_date,
    })) || [];

  //columns for table
  const columns = [
    {
      title: "No.",
      dataIndex: "key", width: 60,
      rowScope: "row",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => {
        return (
          <GeneratePdf
            docuType="cos"
            contents={item}
            disabled={item?.status !== "approved"}
          />
        );
      },
    },
  ];

  const viewingDataConfig = [
    {
      key: 1,
      collapseName: "Required Details",
      collapseItems: [
        {
          key: 1,
          name: "start_date",
          label: "Start Date",
        },
        {
          key: 2,
          name: "end_date",
          label: "End Date",
        },
        {
          key: 3,
          name: "absent",
          label: "Date of Absences",
        },
      ],
    },
    {
      key: 1,
      collapseName: "Additional Details",
      collapseItems: [
        {
          key: 1,
          name: "act1",
          label: "Activity 1",
        },
        {
          key: 2,
          name: "week1_hours",
          label: "No. of hours per week 1",
        },
        {
          key: 3,
          name: "act2",
          label: "Activity 2",
          hideIfEmpty: true,
        },
        {
          key: 4,
          name: "week2_hours",
          label: "No. of hours per week 2",
          hideIfEmpty: true,
        },
        {
          key: 5,
          name: "act3",
          label: "Activity 3",
          hideIfEmpty: true,
        },
        {
          key: 6,
          name: "week3_hours",
          label: "No. of hours per week 3",
          hideIfEmpty: true,
        },
        {
          key: 7,
          name: "act4",
          label: "Activity 4",
          hideIfEmpty: true,
        },
        {
          key: 8,
          name: "week4_hours",
          label: "No. of hours per week 4",
          hideIfEmpty: true,
        },
        {
          key: 9,
          name: "act5",
          label: "Activity 5",
          hideIfEmpty: true,
        },
        {
          key: 10,
          name: "week5_hours",
          label: "No. of hours per week 5",
          hideIfEmpty: true,
        },
      ],
    },
  ];

  return (
    <>
      {loading && "loading..."}
      {!loading && (
        <>
          <Space>
            <p>Spectate: </p>
            <Select
              value={selectedPos}
              options={mappedEmpPos}
              onChange={handlePosSelect}
              disabled={!hasActiveSpectatorPosition}
              popupMatchSelectWidth={false}
            />
          </Space>
          {!hasActiveSpectatorPosition && (
            <p style={{ marginTop: 8, color: "#8c8c8c" }}>
              No active spectator position available.
            </p>
          )}
          <SpectatorTable
            tableColumn={columns}
            tableData={mappedData}
            popupSlice={spectatorCosPopup}
            popupName="spectatorCosPopup"
            docuType="Local Travel Request"
            viewingDataConfig={viewingDataConfig}
          />
        </>
      )}
    </>
  );
};

export default SpectateCos;
